import type { FC } from 'react';
import { useCallback, useState } from 'react';

import { useGetPerson } from '@/features/api/hooks/participant/useGetPerson';
import { usePersonExtendedUpdate } from '@/features/api/hooks/participant/usePersonExtendedUpdate';
import { ProfileEditor } from '@/features/profile-setting/components/ProfileEditor';
import type { PersonUpdate } from '@/features/profile-setting/types/person';
import type { AppNativeStackScreenProps } from '@/navigation/types/navigation';
import { AppRoute, TabRoute } from '@/navigation/types/navigation';

type Props = AppNativeStackScreenProps<typeof AppRoute.ProfileEdit>;

export const ProfileEditScreen: FC<Props> = ({ navigation }) => {
  const { data: person } = useGetPerson();
  const { mutate: updatePerson, isPending: isUpdatePending } =
    usePersonExtendedUpdate();

  const [updatedPerson, setUpdatedPerson] = useState<PersonUpdate>({
    firstName: person?.firstName,
    lastName: person?.lastName,
    homeAreaId: person?.homeAreaId,
    birthYear: person?.birthYear
  });

  const handlePersonUpdates = useCallback((personUpdate: PersonUpdate) => {
    setUpdatedPerson((prevValue) => ({ ...prevValue, ...personUpdate }));
  }, []);

  const handleUpdatePerson = useCallback(
    () =>
      // UI prevents update button if values are empty
      updatePerson(
        {
          firstName: updatedPerson?.firstName ?? '',
          lastName: updatedPerson?.lastName ?? '',
          homeAreaId: updatedPerson?.homeAreaId ?? '',
          birthYear: updatedPerson?.birthYear,
          profilePicture: updatedPerson.temporaryMediaItemId
            ? { temporaryMediaItemId: updatedPerson.temporaryMediaItemId }
            : updatedPerson.profilePicture
        },
        {
          onSuccess: () =>
            navigation?.popTo(AppRoute.Tabs, {
              screen: TabRoute.Profile
            })
        }
      ),
    [updatePerson, updatedPerson, navigation]
  );

  return (
    <ProfileEditor
      person={person}
      disableInput={isUpdatePending}
      onChange={handlePersonUpdates}
      onSave={handleUpdatePerson}
    />
  );
};
